/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path pages
 * @description Login page — UMS auth with silent app-server prepare
 *
 * @section 4.1.2
 *
 */
import { useAuth } from "@qpr/client-library";
import React, { useState } from "react";
import { ensureServerReady } from "../api/serverStartup";
import Login from "./../login/Login";
import ServerPreparingPage from "./ServerPreparingPage";

const LoginPage = () => {
  const { login, auth } = useAuth();
  const [isPreparing, setIsPreparing] = useState(false);
  const [prepareError, setPrepareError] = useState<string | null>(null);
  const [pendingCredentials, setPendingCredentials] = useState<{
    logOnName: string;
    password: string;
  } | null>(null);

  const prepareAndLogin = async (logOnName: string, password: string) => {
    setIsPreparing(true);
    setPrepareError(null);
    setPendingCredentials({ logOnName, password });
    try {
      await ensureServerReady();
      login(logOnName, password);
      // Keep preparing overlay until redirect or auth error (see effect below).
    } catch (error) {
      console.error("Error preparing server for login:", error);
      setPrepareError(
        error instanceof Error
          ? error.message
          : "Unable to prepare session. Please try again.",
      );
      setIsPreparing(false);
    }
  };

  React.useEffect(() => {
    if (auth.authError && isPreparing) {
      setIsPreparing(false);
    }
  }, [auth.authError, isPreparing]);

  const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const logOnName = e.currentTarget.email.value;
    const password = e.currentTarget.password.value;
    void prepareAndLogin(logOnName, password);
  };

  if (isPreparing || prepareError) {
    return (
      <ServerPreparingPage
        message={
          prepareError
            ? undefined
            : "Please wait while we start the server. This can take several minutes — we will keep trying until it is ready..."
        }
        error={prepareError}
        onRetry={
          pendingCredentials
            ? () =>
                void prepareAndLogin(
                  pendingCredentials.logOnName,
                  pendingCredentials.password,
                )
            : undefined
        }
      />
    );
  }

  return (
    <Login
      handleSubmit={submitHandler}
      errorMessage={auth.authError}
    />
  );
};
export default LoginPage;
